
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Shield, Settings, Network, Users, Trash2, ArrowLeft, Globe, Star } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { LevelRestrictionsTab } from "./LevelRestrictionsTab";
import { LevelAppsTab } from "./LevelAppsTab";
import { LevelNetworkRulesTabWithErrorBoundary } from "./LevelNetworkRulesTabWithErrorBoundary";
import { LevelFeaturesTab } from "./LevelFeaturesTab";
import { LevelScopeTab } from "./LevelScopeTab";
import { LevelBrowserTab } from "./LevelBrowserTab";
import { DeleteLevelDialog } from "./DeleteLevelDialog";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { toast } from "sonner";

// Level interface matching Convex API response exactly
interface Level {
  _id: string;
  name: string;
  description?: string;
  private: boolean;
  _creationTime: number;
  allowedApps: string[];
  blockedApps: string[];
  appFilter: Record<string, any>;
  restrictions: string[];
}

interface LevelDetailViewProps {
  level: Level;
  onBack: () => void;
}

const tabItems = [
  { id: "restrictions", label: "Restrictions", icon: Shield },
  { id: "apps", label: "Apps", icon: Settings },
  { id: "browser", label: "Browser", icon: Globe },
  { id: "features", label: "Features", icon: Star },
  { id: "scope", label: "Scope", icon: Users },
];

export const LevelDetailView = ({ level: initialLevel, onBack }: LevelDetailViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("restrictions");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(initialLevel);

  // Update local state when prop changes
  useEffect(() => {
    setCurrentLevel(initialLevel);
  }, [initialLevel]);

  // Restore active tab from URL params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['restrictions', 'apps', 'browser', 'features', 'scope'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Save active tab to URL params
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);
  };

  const updatePrivacyMutation = useConvexMutation(api.levels.visibility.update);

  const handlePrivacyChange = (newPrivateStr: string) => {
    const newPrivate = newPrivateStr === 'private';
    console.log(`Privacy change requested: ${currentLevel.private} -> ${newPrivate}`);
    if (newPrivate !== currentLevel.private) {
      // Optimistically update the local state immediately
      console.log(`Optimistically updating private status to ${newPrivate}`);
      setCurrentLevel(prev => ({
        ...prev,
        private: newPrivate
      }));

      updatePrivacyMutation({
        level: currentLevel._id as any,
        private: newPrivate
      })
      .then(() => {
        toast.success(`Level privacy updated to ${newPrivate ? 'private' : 'public'}`);
        console.log("Level privacy update confirmed from server");
      })
      .catch((error) => {
        console.error("Failed to update level privacy:", error);
        
        // Revert the optimistic update
        setCurrentLevel(prev => ({
          ...prev,
          private: !newPrivate
        }));
        
        toast.error(`Failed to update level privacy: ${error.message || 'Unknown error'}`);
      });
    }
  };

  const handleLevelDeleted = () => {
    onBack();
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Consolidated Header */}
      <div className="relative h-16">
        {/* Gray line that aligns with sidebar */}
        <div className="absolute top-4 left-0 right-0 h-px bg-gray-200"></div>
        
        {/* Header content with white background and bottom border */}
        <div className="relative bg-white h-full border-b border-gray-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 px-1 py-1 h-7"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="text-xs">Back</span>
            </Button>
            
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-semibold text-gray-900">{currentLevel.name}</h1>
              <Badge className={`${currentLevel.private ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'} text-xs px-2 py-1`}>
                {currentLevel.private ? 'private' : 'public'}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Visibility:</span>
              <Select 
                value={currentLevel.private ? 'private' : 'public'} 
                onValueChange={handlePrivacyChange}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="px-2 py-0 text-xs">Public</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="px-2 py-0 text-xs">Private</Badge>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Level
            </Button>
          </div>
        </div>
      </div>

      {/* Main content without horizontal padding */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {currentLevel.description && (
          <div className="py-2 bg-gray-50 border-b px-6 flex-shrink-0">
            <p className="text-gray-600">{currentLevel.description}</p>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
          <div className="border-b border-gray-200 flex-shrink-0">
            <TabsList className="grid w-full grid-cols-5 bg-transparent h-auto p-0 rounded-none">
              {tabItems.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger 
                    key={item.id}
                    value={item.id}
                    className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-4 text-sm flex items-center gap-2"
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <TabsContent value="restrictions" className="h-full m-0 overflow-hidden">
              <div className="h-full overflow-auto">
                <LevelRestrictionsTab levelId={currentLevel._id} restrictions={currentLevel.restrictions} />
              </div>
            </TabsContent>

            <TabsContent value="apps" className="h-full m-0 overflow-hidden">
              <div className="h-full overflow-auto">
                <LevelAppsTab 
                  levelId={currentLevel._id}
                  appFilter={currentLevel.appFilter}
                  allowedApps={currentLevel.allowedApps || []}
                  blockedApps={currentLevel.blockedApps || []}
                />
              </div>
            </TabsContent>

            <TabsContent value="browser" className="h-full m-0 overflow-hidden">
              <div className="h-full overflow-auto">
                <LevelBrowserTab levelId={currentLevel._id} />
              </div>
            </TabsContent>

            <TabsContent value="features" className="h-full m-0 overflow-hidden">
              <div className="h-full overflow-auto">
                <LevelFeaturesTab levelId={currentLevel._id} />
              </div>
            </TabsContent>

            <TabsContent value="scope" className="h-full m-0 overflow-hidden">
              <div className="h-full overflow-auto">
                <LevelScopeTab levelId={currentLevel._id} />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </div>

      <DeleteLevelDialog
        levelId={currentLevel._id}
        levelName={currentLevel.name}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDeleted={handleLevelDeleted}
      />
    </div>
  );
};
